import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthProvider';
import { getIdleTimeoutMs } from '../config/appConfig';

// Custom hook to handle session timeout based on user inactivity
export function useIdleSessionTimeout() {
  const { logout, isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Get idle timeout from centralized configuration (in milliseconds)
  const idleTimeoutMs = getIdleTimeoutMs();

  // Handle session timeout
  const handleTimeout = useCallback(() => {
    // Clear local session data
    localStorage.clear();
    sessionStorage.clear();
    
    // Call logout to clear backend session
    logout();
    
    // Redirect to session-expired page
    navigate('/session-expired', { replace: true });
  }, [logout, navigate]);

  // Reset the timer
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!isAuthenticated) return;
    timerRef.current = setTimeout(() => {
      handleTimeout();
    }, idleTimeoutMs);
  }, [idleTimeoutMs, handleTimeout, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // List of events that indicate user activity
    const events = ['mousemove', 
                    'mousedown', 
                    'keydown', 
                    'scroll', 
                    'touchstart',
                    'click',
                    'focus',
                    'blur'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer(); // Start timer on mount
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [resetTimer, isAuthenticated]);
}
